import { get, post, request } from '../request';
import {
  IMAGE_GENERATION_TIMEOUT_MS,
} from '../utils/seedanceConstants';
import {
  getCachedTokenUsageCheck,
  setCachedTokenUsageCheck,
} from '../utils/seedanceTokenUsageCache';
import { getEnvBaseUrl } from '../utils/envUtils';

const BASE_URL = process.env.REACT_APP_BASE_URL || '/proxy/base';
const ASSETS_URL = process.env.REACT_APP_ASSETS_URL || '/proxy/assets';

type SeedanceMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface SeedanceRequestOptions {
  apiKey: string;
  baseURL?: string;
  assetsURL?: string;
  onDebug?: (message: string, isError?: boolean) => void;
}

/** 任务列表 /api/task/self 分页（与后端 query：p、page_size、task_id、start_timestamp、end_timestamp） */
export interface GetSeedanceTaskListOptions extends SeedanceRequestOptions {
  /**
   * 页码，从 1 开始。若网关实际为 0 起算，可改为传入 page-1 或由后端文档调整。
   */
  page?: number;
  pageSize?: number;
  /** 按任务 id 筛选；空串仍传递，与网关约定一致。 */
  taskId?: string;
  /** Unix 秒；与 endTimestamp 同时传入时限定列表时间范围（如最近 24 小时）。 */
  startTimestamp?: number;
  endTimestamp?: number;
}

interface VideoModelRequestOptions {
  baseUrl: string;
  apiKey: string;
}

/**
 * 文生图（Gemini generateContent、OpenAI Images）服务端推理可能超过默认 30s；全局默认见 `request/index.ts`。
 */
const GEMINI_IMAGE_GENERATION_TIMEOUT_MS = IMAGE_GENERATION_TIMEOUT_MS;
const OPENAI_IMAGE_GENERATION_TIMEOUT_MS = IMAGE_GENERATION_TIMEOUT_MS;

/**
 * Gemini 原生文生图 / 参考图生图。
 * 请求体对齐 `POST /v1beta/models/{model}:generateContent/`（contents + generationConfig），见 33dd Gemini 原生文档。
 */
export interface GeminiNativeImagePayload {
  model: string;
  prompt: string;
  /** 画面比例，如 1:1、16:9 */
  aspect_ratio?: string;
  /** 分辨率档位：1K、2K、4K 等，原样写入 imageConfig.imageSize（优先于 image_size） */
  resolution?: string;
  /** 档位文案（如 2K/4K），在无 width/height、resolution 且非 WxH 时原样写入 imageConfig.imageSize */
  image_size?: string;
  /** 生成张数（组图）；映射 generationConfig.candidateCount */
  number_of_images?: number;
  /** OpenAI 兼容别名 */
  n?: number;
  /** 像素尺寸 → imageConfig.imageSize，优先于 image_size */
  width?: number;
  height?: number;
  /** 单张参考图 base64（不含 data: 前缀） */
  reference_image_base64?: string;
  reference_mime_type?: string;
  [key: string]: unknown;
}

/** 文生图等与 OpenAI Images API 对齐的常见字段；网关可忽略未知字段。 */
export interface ImageGenerationPayload {
  model: string;
  prompt: string;
  /** 例如 1024x1024、1:1 */
  size?: string;
  n?: number;
  response_format?: 'url' | 'b64_json';
  quality?: string;
  [key: string]: unknown;
}

/** @deprecated 旧 JSON 体图生图用；现改为 multipart 文件字段 `image` */
export type ImageEditInputImage = {
  image_url?: string;
  file_id?: string;
};

/**
 * `POST /v1/images/edits`（multipart/form-data）。
 * 网关要求 `image` 为**文件**字段；多图时多次 append 同名 `image`。
 */
export interface ImageEditMultipartPayload {
  prompt: string;
  model?: string;
  n?: number;
  size?: string;
  /** 参考图（纯 base64 + mime），在客户端转为 File 后写入 form */
  imageRefs: { base64: string; mime: string }[];
  background?: 'transparent' | 'opaque' | 'auto';
  input_fidelity?: 'high' | 'low';
  quality?: 'low' | 'medium' | 'high' | 'auto';
  moderation?: 'low' | 'auto';
  output_compression?: number;
  output_format?: 'png' | 'jpeg' | 'webp';
  stream?: boolean;
  user?: string;
}

/** Seedance content 中的多模态条目。 */
export type VideoGenerationMetadataItem =
  | { type: 'text'; text: string }
  | {
      type: 'image_url';
      image_url: { url: string };
      role: 'reference_image';
    }
  | {
      type: 'video_url';
      video_url: { url: string };
      role: 'reference_video';
    }
  | {
      type: 'audio_url';
      audio_url: { url: string };
      role: 'reference_audio';
    };

/** 业务层组装视频生成请求；由 {@link buildVideoGenerationRequestBody} 转为网关 JSON */
export interface VideoGenerationPayload {
  model: string;
  /** 生成文案，写入 content 首条 type=text。 */
  prompt?: string;
  /** 同 prompt，兼容旧字段名 */
  content?: string;
  duration: number;
  ratio?: string;
  resolution?: string;
  /** 参考素材（不含 text），写入 content。 */
  metadataItems?: VideoGenerationMetadataItem[];
  /** @deprecated 改用 ratio */
  width?: number;
  height?: number;
  fps?: number;
  seed?: number;
  n?: number;
  user?: string;
  generate_audio?: boolean;
  watermark?: boolean;
  callback_url?: string;
  return_last_frame?: boolean;
  draft?: boolean;
  tools?: unknown;
  safety_identifier?: string;
  service_tier?: string;
  execution_expires_after?: number;
  frames?: number;
  camera_fixed?: boolean;
  [key: string]: unknown;
}

/** 实际 POST 的请求体 */
export interface VideoGenerationRequestBody {
  model: string;
  content: VideoGenerationMetadataItem[];
  generate_audio?: boolean;
  ratio?: string;
  duration?: number;
  watermark?: boolean;
  resolution?: string;
  seed?: number;
  camera_fixed?: boolean;
  return_last_frame?: boolean;
}

const shouldOmitMetadataValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
};

const resolvePromptText = (raw: Record<string, unknown>): string => {
  const fromContent = typeof raw.content === 'string' ? raw.content.trim() : '';
  const fromPrompt = typeof raw.prompt === 'string' ? raw.prompt.trim() : '';
  return fromContent || fromPrompt;
};

/**
 * 组装新 Seedance 创建任务请求体：
 * - 文案与参考素材均放在顶层 content
 * - 保留调用方选择的模型，不再按分辨率替换旧模型别名
 * - 仅发送新接口文档支持的生成参数
 */
export function buildVideoGenerationRequestBody(
  payload: VideoGenerationPayload,
): VideoGenerationRequestBody {
  const raw = payload as Record<string, unknown>;
  const content: VideoGenerationMetadataItem[] = [];

  const promptText = resolvePromptText(raw);
  if (promptText) {
    content.push({ type: 'text', text: promptText });
  }

  const items = raw.metadataItems;
  if (Array.isArray(items)) {
    for (const item of items) {
      if (item && typeof item === 'object') content.push(item as VideoGenerationMetadataItem);
    }
  }

  const body: VideoGenerationRequestBody = {
    model: String(raw.model).trim(),
    content,
  };
  if (!shouldOmitMetadataValue(raw.duration)) body.duration = Number(raw.duration);
  if (!shouldOmitMetadataValue(raw.ratio)) body.ratio = String(raw.ratio);
  if (!shouldOmitMetadataValue(raw.resolution)) body.resolution = String(raw.resolution);
  for (const key of [
    'generate_audio',
    'watermark',
    'seed',
    'camera_fixed',
    'return_last_frame',
  ] as const) {
    if (!shouldOmitMetadataValue(raw[key])) body[key] = raw[key] as never;
  }
  return body;
}

export interface SeedanceTokenUsageResponse {
  code: boolean;
  data?: {
    expires_at: number;
    model_limits: Record<string, unknown>;
    model_limits_enabled: boolean;
    name: string;
    object: string;
    total_available: number;
    total_granted: number;
    total_used: number;
    unlimited_quota: boolean;
  };
  message?: string;
}

const cleanApiKey = (apiKey: string) => {
  return apiKey.trim().replace(/^Bearer\s+/i, '').replace(/[^a-zA-Z0-9-]/g, '');
};

const getSeedanceBaseURL = (endpoint: string, options: SeedanceRequestOptions) => {
  return endpoint.startsWith('/assets')
    ? options.assetsURL || ASSETS_URL
    : options.baseURL || BASE_URL;
};

export const seedanceRequest = async <T = any, TData = any>(
  method: SeedanceMethod,
  endpoint: string,
  data: TData | null,
  options: SeedanceRequestOptions,
) => {
  const apiKey = cleanApiKey(options.apiKey);
  if (!apiKey) throw new Error('未设置 API Key');

  const baseURL = getSeedanceBaseURL(endpoint, options);
  options.onDebug?.(`${method} ${baseURL}${endpoint}`);
  if (data) options.onDebug?.(`Body: ${JSON.stringify(data).slice(0, 200)}...`);

  try {
    const response = await request<T, TData>(endpoint, {
      baseURL,
      method,
      data: data ?? undefined,
      token: apiKey,
      responseType: 'json',
    });
    options.onDebug?.(`Response OK: ${JSON.stringify(response).slice(0, 200)}`);
    return response;
  } catch (error: any) {
    options.onDebug?.(`Response Error: ${error.message}`, true);
    throw error;
  }
};

export const checkSeedanceConnection = (options: SeedanceRequestOptions) => {
  const apiKey = cleanApiKey(options.apiKey);
  const cached = getCachedTokenUsageCheck(apiKey);
  if (cached) {
    options.onDebug?.('Token 校验命中缓存，跳过 GET /api/usage/token/');
    return Promise.resolve(cached);
  }

  return seedanceRequest<SeedanceTokenUsageResponse>('GET', '/api/usage/token/', null, options).then(
    (response) => {
      if (response.code !== true) {
        throw new Error(response.message || 'Token 校验失败');
      }
      setCachedTokenUsageCheck(apiKey, response);
      return response;
    },
  );
};

export const getSeedanceTaskDetail = (taskId: string, options: SeedanceRequestOptions) => {
  return seedanceRequest('GET', `/contents/generations/tasks/${taskId}`, null, options);
};

export const getSeedanceTaskList = (options: GetSeedanceTaskListOptions) => {
  const systemToken =
    typeof window !== 'undefined' ? (window.localStorage.getItem('ss_system_token') || '').trim() : '';
  const userId =
    typeof window !== 'undefined' ? (window.localStorage.getItem('ss_user_id') || '').trim() : '';

  if (!systemToken || !userId) {
    throw new Error('请先配置系统访问令牌和用户ID');
  }

  const baseURL = options.baseURL || BASE_URL;
  const page = options.page != null && options.page > 0 ? Math.floor(options.page) : 1;
  const pageSize =
    options.pageSize != null && options.pageSize > 0 ? Math.floor(options.pageSize) : 20;
  const taskId = options.taskId !== undefined ? String(options.taskId) : '';

  const params: Record<string, string | number> = {
    p: page,
    page_size: pageSize,
    task_id: taskId,
  };
  if (
    options.startTimestamp != null &&
    Number.isFinite(options.startTimestamp) &&
    options.endTimestamp != null &&
    Number.isFinite(options.endTimestamp)
  ) {
    params.start_timestamp = Math.floor(options.startTimestamp);
    params.end_timestamp = Math.floor(options.endTimestamp);
  }

  options.onDebug?.(`GET ${baseURL}/api/task/self ${JSON.stringify(params)}`);
  options.onDebug?.('Headers: Authorization, New-Api-User');

  return request('/api/task/self', {
    baseURL,
    method: 'GET',
    params,
    headers: {
      Authorization: `Bearer ${systemToken.replace(/^Bearer\s+/i, '')}`,
      'New-Api-User': userId,
    },
    responseType: 'json',
  });
};

export const createSeedanceTask = <TData = any>(data: TData, options: SeedanceRequestOptions) => {
  return seedanceRequest('POST', '/contents/generations/tasks', data, options);
};

export const createVideoGeneration = (
  payload: VideoGenerationPayload,
  options: VideoModelRequestOptions,
) => {
  const body = buildVideoGenerationRequestBody(payload);
  return post<any, VideoGenerationRequestBody>('/v1/video/generations', body, {
    baseURL: options.baseUrl,
    token: cleanApiKey(options.apiKey),
    responseType: 'json',
  });
};

export const createImageGeneration = (
  payload: ImageGenerationPayload,
  options: VideoModelRequestOptions,
) => {
  return post<any, ImageGenerationPayload>('/v1/images/generations', payload, {
    baseURL: options.baseUrl,
    token: cleanApiKey(options.apiKey),
    responseType: 'json',
    timeout: OPENAI_IMAGE_GENERATION_TIMEOUT_MS,
  });
};

/** 将参考图转为 Edits multipart 中的 `File`（字段名 `image`）。 */
export function imageRefToEditFile(ref: { base64: string; mime: string }, index: number): File {
  const mime = (ref.mime || 'image/png').trim();
  const b64 = ref.base64.replace(/\s/g, '');
  let binary: string;
  try {
    binary = atob(b64);
  } catch {
    throw new Error('参考图 base64 无效');
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  const ext = /jpe?g/i.test(mime) ? 'jpg' : /webp/i.test(mime) ? 'webp' : /gif/i.test(mime) ? 'gif' : 'png';
  return new File([blob], `reference_${index}.${ext}`, { type: mime });
}

export function buildImageEditFormData(payload: ImageEditMultipartPayload): FormData {
  const fd = new FormData();
  if (payload.model) fd.append('model', payload.model);
  fd.append('prompt', payload.prompt);
  if (payload.n != null) fd.append('n', String(payload.n));
  if (payload.size) fd.append('size', payload.size);
  if (payload.background) fd.append('background', payload.background);
  if (payload.input_fidelity) fd.append('input_fidelity', payload.input_fidelity);
  if (payload.quality) fd.append('quality', payload.quality);
  if (payload.moderation) fd.append('moderation', payload.moderation);
  if (payload.output_compression != null) {
    fd.append('output_compression', String(payload.output_compression));
  }
  if (payload.output_format) fd.append('output_format', payload.output_format);
  if (payload.stream != null) fd.append('stream', String(payload.stream));
  if (payload.user) fd.append('user', payload.user);

  payload.imageRefs.forEach((ref, i) => {
    fd.append('image', imageRefToEditFile(ref, i));
  });
  return fd;
}

/** @deprecated 请使用 multipart：`imageRefToEditFile` / `buildImageEditFormData` */
export function imageEditImageUrlFromReference(ref: {
  base64: string;
  mime: string;
}): string {
  const mime = (ref.mime || 'image/png').trim();
  const b64 = ref.base64.replace(/\s/g, '');
  return `data:${mime};base64,${b64}`;
}

export const createImageEdit = (
  payload: ImageEditMultipartPayload,
  options: VideoModelRequestOptions,
) => {
  const fd = buildImageEditFormData(payload);
  return post<any, FormData>('/v1/images/edits', fd, {
    baseURL: options.baseUrl,
    token: cleanApiKey(options.apiKey),
    responseType: 'json',
    timeout: OPENAI_IMAGE_GENERATION_TIMEOUT_MS,
  });
};

const GEMINI_IMAGE_GENERATE_TEMPLATE_DEFAULT = '/v1beta/models/{model}:generateContent/';

/** 解析文生图接口路径；默认含 `{model}` 占位符（URL 编码后替换）。 */
export function resolveGeminiNativeImageEndpoint(modelId: string): string {
  const raw =
    typeof process !== 'undefined' ? process.env.REACT_APP_GEMINI_NATIVE_IMAGE_PATH?.trim() : '';
  const template = raw || GEMINI_IMAGE_GENERATE_TEMPLATE_DEFAULT;
  if (template.includes('{model}')) {
    return template.replace(/\{model\}/g, encodeURIComponent(modelId));
  }
  return template;
}

function buildGeminiGenerateContentImageBody(payload: GeminiNativeImagePayload): Record<string, unknown> {
  const n = Math.min(8, Math.max(1, payload.n ?? payload.number_of_images ?? 1));

  const parts: Array<Record<string, unknown>> = [];
  const refB64 = payload.reference_image_base64;
  const refMime = payload.reference_mime_type;
  if (
    typeof refB64 === 'string' &&
    refB64.length > 0 &&
    typeof refMime === 'string' &&
    refMime.length > 0
  ) {
    parts.push({
      inlineData: {
        mimeType: refMime,
        data: refB64.replace(/\s/g, ''),
      },
    });
  }
  parts.push({ text: payload.prompt });

  const w = typeof payload.width === 'number' ? payload.width : undefined;
  const h = typeof payload.height === 'number' ? payload.height : undefined;
  let imageSize: string;
  if (w != null && h != null && w > 0 && h > 0) {
    imageSize = `${Math.round(w)}x${Math.round(h)}`;
  } else if (typeof payload.resolution === 'string' && payload.resolution.trim()) {
    imageSize = payload.resolution.trim();
  } else if (
    typeof payload.image_size === 'string' &&
    /^\d+\s*x\s*\d+$/i.test(payload.image_size.trim())
  ) {
    imageSize = payload.image_size.trim().replace(/\s*x\s*/i, 'x');
  } else if (typeof payload.image_size === 'string' && payload.image_size.trim()) {
    imageSize = payload.image_size.trim();
  } else {
    imageSize = '1024x1024';
  }

  const imageConfig: Record<string, unknown> = { imageSize };
  if (payload.aspect_ratio) {
    imageConfig.aspectRatio = payload.aspect_ratio;
  }

  const generationConfig: Record<string, unknown> = {
    responseModalities: ['IMAGE'],
    imageConfig,
  };
  if (n > 1) {
    generationConfig.candidateCount = n;
  }

  const contentEntry =
    payload.model.startsWith('gemini-2.5')
      ? { parts }
      : { role: 'user', parts };

  return {
    contents: [contentEntry],
    generationConfig,
  };
}

/**
 * Gemini 原生图片生成（generateContent）。
 * 默认 POST `/v1beta/models/{model}:generateContent/`；可用 `REACT_APP_GEMINI_NATIVE_IMAGE_PATH` 覆盖（建议仍包含 `{model}`）。
 */
export const createGeminiNativeImageGeneration = (
  payload: GeminiNativeImagePayload,
  options: VideoModelRequestOptions,
) => {
  const path = resolveGeminiNativeImageEndpoint(payload.model);
  const body = buildGeminiGenerateContentImageBody(payload);
  return post<any, Record<string, unknown>>(path, body, {
    baseURL: options.baseUrl,
    token: cleanApiKey(options.apiKey),
    responseType: 'json',
    timeout: GEMINI_IMAGE_GENERATION_TIMEOUT_MS,
  });
};

/**
 * 查询新 Seedance 视频生成任务状态；轮询直接请求中转站，避免经过 agiBack。
 */
export const getVideoGenerationTask = (
  taskId: string,
  options: VideoModelRequestOptions,
) => {
  const upstreamBase = process.env.REACT_APP_BASE_URL || '/proxy/base';
  return get<any>(`/seedance/api/v3/contents/generations/tasks/${encodeURIComponent(taskId)}`, {
    baseURL: upstreamBase,
    token: cleanApiKey(options.apiKey),
    responseType: 'json',
  });
};
